import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../services/api';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface UnderlyingMetrics {
  symbol: string;
  displayName: string;
  spotPrice: number;
  atmIv: number;
  ivRank52w: number;
  ivRank30d: number;
  hv30d: number;
  pcr: number;
  pcrOi: number;
  maxPainStrike: number;
  straddlePrice: number;
  gexTotal: number;
  gexFlipPrice: number;
  gexByStrike: Record<string, number>;
  oiClassification: Record<string, any[]>;
  ceVwapOi: number;
  peVwapOi: number;
  computedAt: string;
  dataStale: boolean;
  feedSource: string;
}

export interface PulseCardData {
  id: number;
  symbol: string;
  displayName: string;
  exchange: string;
  isIndex: boolean;
  lotSize: number;
  feedSource: string;
  latestMetrics: UnderlyingMetrics | null;
  dataStale: boolean;
  latestSignal?: SignalData | null;
}

export interface SignalData {
  id: number;
  underlyingId: number;
  direction: 'LONG' | 'SHORT';
  score: number;
  regimeTag: string; // AUTO-APPROVE | REVIEW | REJECT
  reason: string;
  strategyRecommendation: string;
  triggerConditions: { components: ScoreComponent[] };
  createdAt: string;
  status: string;
  basketId?: number;  // set by backend when a basket was created for this signal
}

export interface ScoreComponent {
  name: string;
  weight: number;
  score: number;
  pass: boolean;
  reason: string;
}

export interface BasketLeg {
  action: 'BUY' | 'SELL';
  symbol: string;
  strike: number;
  optionType: 'CE' | 'PE';
  expiry: string;
  quantity: number;
  ltp: number;
  bidAskSpread: number;
  costPerLeg: number;
}

export interface BasketData {
  id: number;
  signalId: number;
  strategy: string;
  legs: BasketLeg[];
  marginEstimate: number;
  maxProfit: number;
  maxLoss: number;
  breakEvens: number[];
  netPremium: number;
  payoffData: { spotRange: number[]; expiry: number[]; today: number[] };
  status: string;
}

export interface SafetyGate {
  name: string;
  pass: boolean;
  blocking: boolean;
  message: string;
}

// ─── State ───────────────────────────────────────────────────────────────────

interface OptionsState {
  pulseCards: PulseCardData[];
  activeSignal: SignalData | null;
  activeBasket: BasketData | null;
  safetyGates: SafetyGate[];
  labData: Record<string, any>; // keyed by symbol
  loading: boolean;
  error: string | null;
  // data freshness per symbol (ms since epoch)
  dataFreshness: Record<string, number>;
}

const initialState: OptionsState = {
  pulseCards: [],
  activeSignal: null,
  activeBasket: null,
  safetyGates: [],
  labData: {},
  loading: false,
  error: null,
  dataFreshness: {}
};

// ─── Async Thunks ────────────────────────────────────────────────────────────

export const fetchPulseCards = createAsyncThunk('options/fetchPulseCards', async () => {
  const res = await api.get('/options/underlyings');
  return (res.data as any).data as PulseCardData[];
});

export const fetchSnapshot = createAsyncThunk('options/fetchSnapshot', async (symbol: string) => {
  const res = await api.get(`/options/snapshot/${symbol}`);
  return { symbol, data: (res.data as any).data };
});

export const fetchLabData = createAsyncThunk(
  'options/fetchLabData',
  async ({ symbol, expiryId, days }: { symbol: string; expiryId: number; days?: number }) => {
    const d = days != null ? days : 1;
    const res = await api.get(`/options/lab/${symbol}/${expiryId}`, { params: { days: d } });
    return { symbol, data: (res.data as any).data };
  }
);

export const fetchBasket = createAsyncThunk('options/fetchBasket', async (basketId: number) => {
  const res = await api.get(`/options/baskets/${basketId}`);
  return (res.data as any).data;
});

export const approveBasket = createAsyncThunk('options/approveBasket', async (basketId: number) => {
  const res = await api.post(`/options/baskets/${basketId}/approve`);
  return (res.data as any).data;
});

export const rejectBasket = createAsyncThunk(
  'options/rejectBasket',
  async ({ basketId, reason }: { basketId: number; reason?: string }) => {
    const res = await api.post(`/options/baskets/${basketId}/reject`, { reason });
    return (res.data as any).data;
  }
);

export const analyzeSymbol = createAsyncThunk(
  'options/analyzeSymbol',
  async ({ symbol, direction }: { symbol: string; direction: string }) => {
    const res = await api.post('/options/analyze', { symbol, direction });
    return (res.data as any).data;
  }
);

// ─── Slice ───────────────────────────────────────────────────────────────────

const optionsSlice = createSlice({
  name: 'options',
  initialState,
  reducers: {
    // Called from WebSocket option:snapshot events
    updatePulseCardMetrics(state, action: PayloadAction<{
      symbol: string;
      capturedAt: string;
      pcr: number;
      atmIv: number;
      gexTotal: number;
      spotPrice: number;
      maxPainStrike: number;
      straddlePrice: number;
      ivRank52w: number;
    }>) {
      const { symbol, ...metrics } = action.payload;
      const card = state.pulseCards.find(c => c.symbol === symbol);
      if (card?.latestMetrics) {
        Object.assign(card.latestMetrics, metrics, { dataStale: false });
      }
      state.dataFreshness[symbol] = Date.now();
    },
    // Called from WebSocket option:signal events
    setActiveSignal(state, action: PayloadAction<SignalData>) {
      state.activeSignal = action.payload;
      state.activeBasket = null; // clear stale basket
    },
    // Called from WebSocket option:basket events
    setActiveBasket(state, action: PayloadAction<BasketData>) {
      state.activeBasket = action.payload;
    },
    clearActiveSignal(state) {
      state.activeSignal = null;
      state.activeBasket = null;
      state.safetyGates = [];
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPulseCards.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(fetchPulseCards.fulfilled, (state, action) => {
        state.loading = false;
        state.pulseCards = action.payload;
      })
      .addCase(fetchPulseCards.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to load underlyings';
      })
      .addCase(fetchLabData.fulfilled, (state, action) => {
        state.labData[action.payload.symbol] = action.payload.data;
      })
      .addCase(fetchBasket.fulfilled, (state, action) => {
        state.activeBasket = action.payload.basket;
        state.safetyGates = action.payload.gates || [];
      })
      .addCase(approveBasket.fulfilled, (state) => {
        if (state.activeBasket) state.activeBasket.status = 'EXECUTED';
      })
      .addCase(rejectBasket.fulfilled, (state) => {
        if (state.activeBasket) state.activeBasket.status = 'REJECTED';
      })
      .addCase(analyzeSymbol.pending, (state) => { state.loading = true; state.error = null; })
      .addCase(analyzeSymbol.fulfilled, (state, action) => {
        state.loading = false;
        state.activeSignal = action.payload;
        state.activeBasket = null; // clear stale basket from previous analyze
        state.safetyGates = [];
      })
      .addCase(analyzeSymbol.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Analysis failed';
      })
      .addCase(fetchLabData.rejected, (state, action) => {
        state.error = action.error.message || 'Failed to load lab data';
        // Set to null (not undefined) so the UI can distinguish "still loading" vs "failed"
        state.labData[action.meta.arg.symbol] = null;
      });
  }
});

export const { updatePulseCardMetrics, setActiveSignal, setActiveBasket, clearActiveSignal } = optionsSlice.actions;
export default optionsSlice.reducer;
